import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { DnoStatusBadge } from './DnoStatusBadge';
import { dnoAPI } from '../lib/api';
import { Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const OPTIONS = [
  { value: 'not_raised', label: 'Not Raised' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'commissioned', label: 'Commissioned' },
  { value: 'refused', label: 'Refused' },
  { value: 'exempt', label: 'Not Required' },
];

/**
 * Badge + admin pencil popover for the Customer Detail header.
 *
 * - When a live ENA-linked record exists, the badge reflects that and the
 *   pencil is hidden (manual override is ignored anyway).
 * - When no live record exists, admins can set a manual status from the
 *   pencil popover. Empty selection clears the override.
 */
export const DnoStatusEditable = ({ customer, isAdmin = false, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(customer?.dno_manual_status || 'not_raised');
  const [note, setNote] = useState(customer?.dno_manual_status_note || '');
  const [exportLimited, setExportLimited] = useState(!!customer?.dno_export_limited);
  const [exportNote, setExportNote] = useState(customer?.dno_export_limit_note || '');
  const [saving, setSaving] = useState(false);

  const hasLiveENA =
    !!customer?.dno_application_submitted_date ||
    !!customer?.dno_application_confirmed_date ||
    !!customer?.dno_commissioning_confirmed_date ||
    !!customer?.dno_reference;

  const save = async (newStatus, clear = false) => {
    setSaving(true);
    try {
      const res = await dnoAPI.setCustomerManualStatus(
        customer.id,
        clear ? '' : newStatus,
        note,
        { export_limited: clear ? false : exportLimited, export_limit_note: clear ? '' : exportNote },
      );
      toast.success(clear ? 'Manual DNO status cleared' : `DNO status set to ${OPTIONS.find(o => o.value === newStatus)?.label || newStatus}`);
      setOpen(false);
      if (onUpdated) onUpdated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update DNO status');
    } finally {
      setSaving(false);
    }
  };

  // Hide the edit pencil when live ENA data exists — the badge is already
  // sourced from ENA and admins can't override it (per admin choice).
  if (hasLiveENA) {
    return <DnoStatusBadge customer={customer} showWhenAbsent />;
  }

  return (
    <div className="inline-flex items-center gap-1" data-testid="dno-status-editable">
      <DnoStatusBadge customer={customer} showWhenAbsent />
      {isAdmin && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-slate-400 hover:text-slate-700 p-0.5"
              title="Set manual DNO status"
              data-testid="dno-status-edit-btn"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="start">
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Manual DNO Status</Label>
                <p className="text-[11px] text-slate-500 mb-1.5">
                  Only used when no live ENA Connect Direct record is linked.
                </p>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-8 text-xs" data-testid="dno-manual-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value} data-testid={`dno-manual-status-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="e.g. Filed via paper form 2024-11"
                  data-testid="dno-manual-status-note"
                />
              </div>
              <div className="border-t pt-2.5 space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="dno-export-limited"
                    checked={exportLimited}
                    onCheckedChange={(v) => setExportLimited(!!v)}
                    className="mt-0.5"
                    data-testid="dno-export-limited-checkbox"
                  />
                  <Label htmlFor="dno-export-limited" className="text-xs leading-snug cursor-pointer">
                    Export limitation applied to this installation
                  </Label>
                </div>
                {exportLimited && (
                  <Input
                    value={exportNote}
                    onChange={(e) => setExportNote(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="e.g. 3.68 kW export limit / 0 kW (G100)"
                    data-testid="dno-export-limit-note"
                  />
                )}
                <p className="text-[11px] text-slate-500">
                  Shown on the engineer calendar invite so it's configured on the day.
                </p>
              </div>
              <div className="flex justify-between gap-2">
                <Button size="sm" variant="ghost" className="text-rose-600 text-xs h-7" onClick={() => save('', true)} disabled={saving} data-testid="dno-manual-status-clear">
                  Clear
                </Button>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button size="sm" className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => save(status)} disabled={saving} data-testid="dno-manual-status-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default DnoStatusEditable;
